import { useEffect, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { metadataSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { useUpdateMetadataDetailsMutation } from "../../services/api";
import style from "./metadata.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof metadataSchema.__outputType;

type EditMetadataNavState = {
  metadata?: Metadata;
};

/**
 * Edit Metadata page for updating an existing metadata record.
 *
 * Notes:
 * - The row being edited is handed over in router state by the Metadata list,
 *   so this screen does not re-fetch it. Browsers persist history.state, so a
 *   reload still works — but a new tab or a shared link carries no state, which
 *   is why the no-state fallback below sends the user back to the list.
 * - Validation is enforced via `metadataSchema`.
 */
const EditMetadata = () => {
  const [updateMetadata] = useUpdateMetadataDetailsMutation();
  const { id } = useParams();
  const { state } = useLocation();
  const { metadata: metadataDetails } = (state ?? {}) as EditMetadataNavState;
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const navigation = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isSavingMetadata, setIsSavingMetadata] = useState(false);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(metadataSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormState>({
    resolver: yupResolver(metadataSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (metadataDetails) {
      // Reset from the handed-over record to avoid stale edits when navigating
      // between rows without a remount.
      reset({
        name: metadataDetails.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataDetails]);

  /**
   * Submits the updated metadata payload.
   *
   * Args:
   *   values (FormState): Form values for the metadata update.
   *
   * Returns:
   *   Promise<void>
   *
   * Raises:
   *   Error: Propagated when the update mutation fails.
   *
   * Notes:
   * - Uses route `id`; blocks submit when `id` is invalid.
   */
  const onSubmit = async (values: FormState) => {
    try {
      // Block updates if route id is missing/invalid to avoid unintended mutations.
      const parsedId = parseInt(id ?? "", 10);
      if (Number.isNaN(parsedId)) {
        setMessage({
          type: "error",
          text: "Unable to update metadata. Please reload and try again.",
        });
        return;
      }
      setIsSavingMetadata(true);
      const { name } = values;
      const payload: EditMetadataPayload = {
        id: parsedId,
        name: name,
      };

      await updateMetadata(payload).unwrap();
      setIsSavingMetadata(false);
      setMessage({
        type: "success",
        text: "Metadata updated successfully.",
      });
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
      setIsSavingMetadata(false);
    }
  };
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  if (!metadataDetails) {
    return (
      <Box className={style.container}>
        <Typography className={style.detailHeading} sx={{ marginTop: "10px" }}>
          This metadata record is no longer available
        </Typography>
        <Typography sx={{ color: colors.grey, marginTop: "10px" }}>
          Metadata details are carried over from the list screen, so this page
          cannot be opened directly or shared as a link. Open it again from
          Metadata.
        </Typography>
        <Box className={style.buttons}>
          <Button
            sx={{ width: "200px", height: "55px" }}
            variant="contained"
            onClick={() => navigation("/metadata")}
            label={"Back to Metadata"}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <Typography
            className={style.detailHeading}
            sx={{ marginTop: "10px" }}
          >
            Metadata Details
          </Typography>
          <Box className={style.flexTop}>
            <Box className={style.row}>
              <Input
                placeholder="e.g Gross Description"
                label="Name"
                required
                register={register}
                name="name"
                errors={errors}
              />
            </Box>
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label={"Back"}
            />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label={"Save"}
              loading={isSavingMetadata}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default EditMetadata;
